import { usePostMessage } from '../../context/messageContext';
import { useTestContext } from '../../context/testContext';
import { Button } from '../../ui';
import { Column, Question, Row, CountButtons, ValueBox, useConfirmNavigation } from '../../screens/optician/exams/common';

// Kay's Optotypes scoring controls (Figma session/16 left, 30 right). The answer/size logic is
// unchanged: answers adjust the score through the useLogmarReducer messages and move one line down,
// "Previous logMAR" moves one line up, "Repeat" shows a new set of optotypes.
export const useKaysControls = ({ getRandomOptotypes, finishTest, logmarSize, eyeTested, updateStep }) => {
  const postMessage = usePostMessage();
  const confirmNavigation = useConfirmNavigation();
  const {
    updateTestData,
    testData: { optotypesToShow, maxPermittedLogmar },
  } = useTestContext();
  const multipleOptotypes = optotypesToShow > 1;

  const handleAnswer = (numberSeen) => {
    const amountToSubtractFromScore = multipleOptotypes ? numberSeen * 0.02 : 0.1;
    postMessage({ type: 'useLogmarReducer', action: 'adjustFinalScore', payload: amountToSubtractFromScore });
    if (logmarSize.id === 13) {
      return finishTest();
    } else {
      postMessage({ type: 'useLogmarReducer', action: 'decrementLogmarSize' });
      return getRandomOptotypes();
    }
  };

  const previousLogmar = () => {
    getRandomOptotypes();
    return postMessage({ type: 'useLogmarReducer', action: 'incrementLogmarSize' });
  };

  const back = () =>
    confirmNavigation().then(() => {
      updateTestData({ [eyeTested]: null, testInProgress: false });
      updateStep(`${eyeTested}Setup`);
    });

  return {
    multipleOptotypes,
    optotypesToShow,
    handleAnswer,
    previousLogmar,
    //the max permitted logMAR defaults to 1; can we simplify this check?
    previousDisabled: logmarSize.id === 1 || logmarSize === maxPermittedLogmar.id,
    repeat: () => getRandomOptotypes(),
    endTest: () => finishTest(),
    back,
  };
};

/** "How many optotypes seen?" [0 / End Test] [1]-[5] [Repeat] (single optotype: [Correct] [End Test]). */
export const KaysAnswerControls = ({ controls }) => (
  <Column gap={16}>
    <Question>{controls.multipleOptotypes ? 'How many optotypes seen?' : 'Was the optotype seen?'}</Question>
    <Row gap={12}>
      <Button variant='destructive' size='lg' onClick={controls.endTest}>
        {controls.multipleOptotypes ? '0 / End Test' : 'End Test'}
      </Button>
      {!controls.multipleOptotypes && (
        <Button variant='primary' size='lg' onClick={() => controls.handleAnswer(controls.optotypesToShow)}>
          Correct
        </Button>
      )}
    </Row>
    {controls.multipleOptotypes && <CountButtons count={5} onPick={controls.handleAnswer} />}
    <Row>
      <Button variant='secondary' size='lg' onClick={controls.repeat}>
        Repeat
      </Button>
    </Row>
  </Column>
);

/** "Present logMAR Size [1]" + [Previous logMAR]. */
export const KaysSizeControls = ({ controls, logmarValue }) => (
  <Column gap={12}>
    <ValueBox label='Present logMAR Size' value={logmarValue ?? '—'} />
    <Row>
      <Button variant='secondary' size='lg' onClick={controls.previousLogmar} disabled={controls.previousDisabled}>
        Previous logMAR
      </Button>
    </Row>
  </Column>
);

const KaysControls = KaysAnswerControls;
export default KaysControls;
